// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TitleTypography from "@hardwoods/App/Components/Common/TitleTypography";
import ShopByProductComponent from "@hardwoods/App/Components/ShopByProduct/ShopByProduct";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { newGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Tab, { TabPresentationProps } from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    tabs = "tabs",
    title = "title",
    primaryTitle = "primaryTitle",
    secondaryTitle = "secondaryTitle",
    products = "products",
}

interface ProductModel {
    fields: {
        productId: string;
    };
}

interface TabsProductModel {
    title: string;
    products: string[];
}

interface TabsModel {
    fields: {
        title: string;
        products: ProductModel[];
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.primaryTitle]: string;
        [fields.secondaryTitle]: string;
        [fields.tabs]: TabsModel[];
    };
}

const mapDispatchToProps = {
    loadProductInfoList,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface ContentFeatureStyles {
    shopByGridContainer?: GridContainerProps;
    shopByGridItem?: GridItemProps;
    title?: TypographyProps;
    centeringWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    tabGroupStyle?: TabGroupPresentationProps;
    tabStyle?: TabPresentationProps;
}

export const shopByStyles: ContentFeatureStyles = {
    shopByGridContainer: {
        gap: 30,
    },
    shopByGridItem: {
        width: [12, 6, 6, 4, 4],
        css: css`
            ${xScreen.lte("xs")} {
                justify-content: center;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            flex-direction: column;
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
        `,
    },
    tabGroupStyle: {
        cssOverrides: {
            wrapper: css`
                align-items: center;
                div[data-id="tabContent"] {
                    width: 100%;
                }
            `,
            tabContent: css`
                border-bottom: 0;
                padding: 30px 0;
            `,
        },
    },
};

const styles = shopByStyles;

const ShopByProduct: FC<Props> = ({ fields, loadProductInfoList }) => {
    const { tabs } = fields;
    const [widgetId] = useState(newGuid());
    const products = useAppSelector(state => getProductsForProductInfoList(state, widgetId));
    const [tabProducts, setTabProducts] = useState<TabsProductModel[]>([]);
    const productsIdsArray: string[] = [];
    const tabProductsArray: TabsProductModel[] = [];

    useEffect(() => {
        tabs.forEach((tab: any) => {
            const { fields } = tab;
            const tabProductsIds =
                fields.products?.length &&
                fields.products?.map((product: any) => {
                    productsIdsArray.push(product.fields.productId);
                    return product.fields.productId;
                });
            tabProductsArray.push({ title: fields.title, products: tabProductsIds });
        });

        setTabProducts(tabProductsArray);
        if (!productsIdsArray || productsIdsArray.length === 0) {
            return;
        }

        loadProductInfoList({
            id: widgetId,
            getProductCollectionParameter: { productNumbers: productsIdsArray, pageSize: "999" } as any,
        });
    }, []);

    if (tabProducts.length === 0 || !products || products?.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.centeringWrapper}>
            {(fields.primaryTitle || fields.secondaryTitle) && (
                <TitleTypography
                    primaryTitle={fields.primaryTitle}
                    secondaryTitle={fields.secondaryTitle}
                    textAlign="center"
                    {...styles.title}
                />
            )}
            <TabGroup current={tabProducts[0]?.title} {...styles.tabGroupStyle}>
                {tabProducts.map((tab, index) => {
                    return (
                        <Tab
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${tab.title}_${index}`}
                            headline={tab.title}
                            tabKey={tab.title}
                            {...styles.tabStyle}
                        >
                            <GridContainer {...styles.shopByGridContainer}>
                                {products
                                    ?.filter(product => tab.products?.includes(product.productNumber))
                                    .map(product => {
                                        return (
                                            <GridItem {...styles.shopByGridItem} key={product.id}>
                                                <ShopByProductComponent product={product} widgetId={widgetId} />
                                            </GridItem>
                                        );
                                    })}
                            </GridContainer>
                        </Tab>
                    );
                })}
            </TabGroup>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(ShopByProduct),
    definition: {
        displayName: "Shop By Product",
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.primaryTitle,
                displayName: "Primary Title",
                editorTemplate: "TextField",
                defaultValue: "Shop By",
                fieldType: "General",
            },
            {
                name: fields.secondaryTitle,
                displayName: "Secondary Title",
                editorTemplate: "TextField",
                defaultValue: "Project",
                fieldType: "General",
            },
            {
                name: fields.tabs,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: fields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.products,
                        editorTemplate: "ListField",
                        defaultValue: [],
                        getDisplay: (item: HasFields) => {
                            return item.fields.productId;
                        },
                        fieldDefinitions: [
                            {
                                name: "productId",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                        ],
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
